import { useCallback, useState, type PointerEvent, type ReactNode } from 'react';

export interface TipState { x: number; y: number; content: ReactNode }

/** Pointer-following tooltip state for a chart wrapper (position relative to the wrapper). */
export function useTooltip() {
  const [tip, setTip] = useState<TipState | null>(null);
  const show = useCallback((e: PointerEvent<Element>, content: ReactNode) => {
    const host = (e.currentTarget as Element).closest('.chart') as HTMLElement | null;
    const r = host?.getBoundingClientRect();
    setTip({ x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0), content });
  }, []);
  /** Position relative to the `.chart` host that contains `el` (used on keyboard focus). */
  const showAt = useCallback((el: Element, content: ReactNode) => {
    const host = el.closest('.chart') as HTMLElement | null;
    const r = host?.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    setTip({ x: b.right - (r?.left ?? 0), y: b.top + b.height / 2 - (r?.top ?? 0), content });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  return { tip, show, showAt, hide };
}

export function Tooltip({ tip, width }: { tip: TipState | null; width: number }) {
  if (!tip) return null;
  const flip = width > 0 && tip.x > width * 0.6;
  return (
    <div
      className="tip"
      role="status"
      style={{ left: tip.x, top: tip.y, transform: `translate(${flip ? 'calc(-100% - 14px)' : '14px'}, -50%)` }}
    >
      {tip.content}
    </div>
  );
}
